import { boxCategoryFromSlug, boxCategorySlug } from "@loot-os/api-contract";
import {
  BOX_SORTS,
  BOX_TABS,
  type BoxSort,
  type BoxTab,
} from "@loot-os/design-system/templates/boxes/boxesContent";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback } from "react";

/**
 * The boxes page's filters as its URL carries them, so a filtered view survives opening a box and
 * coming back. A default is left out of the URL rather than written into it.
 */
export interface BoxesSearch {
  tab?: Exclude<BoxTab, "all">;
  /** A box category, by its slug (`one-piece`); one the list does not have is dropped. */
  category?: string;
  q?: string;
  /** Price bounds, in cents. */
  min?: number;
  max?: number;
  sort?: Exclude<BoxSort, "default">;
  /** Only boxes the shopper's balance covers. */
  enough?: true;
}

function isMember<T extends string>(set: readonly T[], value: unknown): value is T {
  return typeof value === "string" && (set as readonly string[]).includes(value);
}

function cents(value: unknown): number | undefined {
  const n = typeof value === "string" ? Number(value) : value;
  return typeof n === "number" && Number.isSafeInteger(n) && n >= 0 ? n : undefined;
}

/** The route's `validateSearch`: anything it does not recognize is dropped, never an error. */
export function parseBoxesSearch(search: Record<string, unknown>): BoxesSearch {
  const tab = isMember(BOX_TABS, search.tab) && search.tab !== "all" ? search.tab : undefined;
  const sort =
    isMember(BOX_SORTS, search.sort) && search.sort !== "default" ? search.sort : undefined;
  const q = typeof search.q === "string" && search.q !== "" ? search.q : undefined;
  const category = boxCategoryFromSlug(search.category);
  return {
    ...(tab ? { tab } : {}),
    ...(category ? { category: boxCategorySlug(category) } : {}),
    ...(q !== undefined ? { q } : {}),
    ...(cents(search.min) !== undefined ? { min: cents(search.min) } : {}),
    ...(cents(search.max) !== undefined ? { max: cents(search.max) } : {}),
    ...(sort ? { sort } : {}),
    ...(search.enough === true || search.enough === "true" ? { enough: true as const } : {}),
  };
}

/** The boxes page's filters, and a writer that replaces the URL's entry rather than adding one. */
export function useBoxesSearch(): [BoxesSearch, (patch: Partial<BoxesSearch>) => void] {
  const search = useSearch({ from: "/boxes" });
  const navigate = useNavigate({ from: "/boxes" });
  const update = useCallback(
    (patch: Partial<BoxesSearch>) => {
      void navigate({
        search: (prev) => parseBoxesSearch({ ...prev, ...patch }),
        replace: true,
      });
    },
    [navigate],
  );
  return [search, update];
}
